import * as React from 'react';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';
import autobind from 'autobind-decorator';
import { connect } from "react-redux";

import { DataLocalService } from 'src/core/services/data-local.service';
import { Column, DataTableView, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { TextView } from 'src/core/ui/text.view';
import { FormatService } from 'src/core/services/format.service';
import { ObjectUtils } from 'src/core/utils/object';
import { TooltipView } from 'src/core/ui/tooltip.view';

interface Props extends ComponentViewProps<TourItineraryDateItem> {
	mode: ControlMode;
	occupancyColumns: OccupancyColumn[];
	productPriceConfigs: ItineraryDateProductPriceConfig[];
	sellCurrencyIsoCode: string;
	products: TourItineraryProductItem[];
	itinerary: TourItineraryItem;
}

interface PropsFromState {
	currencies: CurrencySummary[];
}

@observer
class TourItineraryDateProductsConfig extends React.Component<Props & PropsFromState, {}> {
	@resolve
	private formatService: FormatService;

	@autobind
	private handleChangePrice(productPrice: ItineraryDateProductPrice, occupancy: OccupancyColumn, priceType: 'cost' | 'sell', value: number): void {
		const { products = [] } = this.props;
		const product = products.find(e => e.id === productPrice.tourItineraryProductId);
		let occupancyPrice = productPrice.occupanciesPrice.find(m => m.occupancyName === occupancy.name);
		if (occupancyPrice == null) {
			occupancyPrice = {
				occupancyName: occupancy.name,
				adults: occupancy.adults,
				children: occupancy.children,
				infants: occupancy.infants,
				cost: 0,
				sell: 0,
				costCurrency: product.supplierDefaultCurrencyIso3Name,
				productProviderKey: product.productProviderApiName,
				supplierId: product.supplierId,
				exchangeRate: 1
			};
			productPrice.occupanciesPrice.push(occupancyPrice);
		}

		occupancyPrice[priceType] = value;
	}

	render(): React.ReactNode {
		const { occupancyColumns, productPriceConfigs, mode, setBusy, currencies } = this.props;

		const columns: Column[] = [{
			title: 'Product',
			key: 'displayName',
			dataIndex: 'displayName'
		},
		{
			title: 'Service',
			key: 'serviceTypeDisplayName',
			dataIndex: 'serviceTypeDisplayName'
		},
		{
			title: 'Duration',
			key: 'duration',
			children: [{
				title: 'From',
				key: 'dateFrom',
				dataIndex: 'dateFrom',
				width: 75,
				render: (field: Date) => this.formatService.toString('small-date', field)
			},
			{
				title: 'To',
				key: 'dateTo',
				dataIndex: 'dateTo',
				width: 75,
				render: (field: Date) => this.formatService.toString('small-date', field)
			}]
		},
		...occupancyColumns.map(m => ({
			title: m.name.toUpperCase(),
			key: m.name,
			children: [
				{
					title: 'Cost',
					key: `cost-${m.name}`,
					dataIndex: 'cost',
					editable: (_, record: ItineraryDateProductPriceConfig, index: number, isSelected: boolean) => {
						const occupancyPrice = record.occupanciesPrice.find(p => p.occupancyName === m.name);
						if (occupancyPrice == null) {
							return 0;
						}

						const value = occupancyPrice.cost;
						const computed = occupancyPrice.computedCost || value;
						const currencyFormat = currencies.find(m => m.iso3Name === occupancyPrice.costCurrency).format;
						if (!isSelected) {
							const computedFormatted = this.formatService.toString('currency', ObjectUtils.round(computed), currencyFormat)
							const valueFormatted = this.formatService.toString('currency', ObjectUtils.round(value), currencyFormat);
							if (computed !== value) {
								return (
									<TooltipView title={`Computed Cost: ${computedFormatted}`}>
										<span className={`mismatch-price-${computed < value ? 'up' : 'down'}`}>
											{valueFormatted}
										</span>
									</TooltipView>
								);
							}

							return valueFormatted;
						}

						return (
							<TextView
								label={null}
								mode={mode}
								value={value}
								targetType="float+"
								onChanged={(newValue) => {
									this.handleChangePrice(record, m, 'cost', newValue);
								}}
							/>
						);
					}
				},
				{
					title: 'Sell',
					key: `sell-${m.name}`,
					dataIndex: 'sell',
					editable: (_, record: ItineraryDateProductPriceConfig, index: number, isSelected: boolean) => {
						const occupancyPrice = record.occupanciesPrice.find(p => p.occupancyName === m.name);
						if (occupancyPrice == null) {
							return 0;
						}

						const value = occupancyPrice.sell;
						const computed = occupancyPrice.computedSell || value;
						const currencyFormat = currencies.find(m => m.iso3Name === this.props.sellCurrencyIsoCode).format;
						if (!isSelected) {
							const computedFormatted = this.formatService.toString('currency', ObjectUtils.round(computed), currencyFormat)
							const valueFormatted = this.formatService.toString('currency', ObjectUtils.round(value), currencyFormat);
							if (computed !== value) {
								return (
									<TooltipView title={`Computed Sell: ${computedFormatted}`}>
										<span className={`mismatch-price-${computed < value ? 'up' : 'down'}`}>
											{valueFormatted}
										</span>
									</TooltipView>
								);
							}

							return valueFormatted;
						}

						return (
							<TextView
								label={null}
								mode={mode}
								value={value}
								targetType="float+"
								onChanged={(newValue) => {
									this.handleChangePrice(record, m, 'sell', newValue);
								}}
							/>
						);
					}
				}
			]
		}))];
		const dataSet = new DataLocalService<any>(productPriceConfigs);

		return (
			<DataTableView
				rowKey={(m) => m.tourItineraryProductId}
				model={dataSet}
				columns={columns}
				mode={mode}
				setBusy={setBusy}
				beforeDeleteItem={() => { }}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideFooter}
				selection={false}
			/>
		);
	}
}

const mapStateToProps = (state: IApplicationState): PropsFromState => {
	return {
		currencies: state.currencies,
	};
};

const connected = connect(
	mapStateToProps,
)(TourItineraryDateProductsConfig);
export { connected as TourItineraryDateProductsConfig };



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-products-config.tsx